import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { api, apiError } from "../api/client";
import { useAuth } from "../store/auth";

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/api/auth/login", { email, password });
      setSession(data.token, data.user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4">
      <div className="absolute right-4 top-4">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div
            className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-fuchsia-600 shadow-lg shadow-brand-900/40"
            aria-hidden="true"
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="white">
              <rect x="3.5" y="14" width="3" height="6.5" rx="1" />
              <rect x="9" y="10" width="3" height="10.5" rx="1" />
              <rect x="14.5" y="6" width="3" height="14.5" rx="1" />
              <circle cx="20" cy="4.7" r="2.2" />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold">{t("auth.login.title")}</h1>
          <p className="mt-1 text-sm text-slate-400">{t("auth.login.subtitle")}</p>
        </div>
        <form onSubmit={onSubmit} className="card space-y-4">
          <div>
            <label className="label" htmlFor="email">
              {t("auth.login.email")}
            </label>
            <input
              id="email"
              type="email"
              required
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </div>
          <div>
            <label className="label" htmlFor="password">
              {t("auth.login.password")}
            </label>
            <input
              id="password"
              type="password"
              required
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>
          <button type="submit" className="btn-primary w-full" disabled={loading}>
            {loading ? t("auth.login.submitting") : t("auth.login.submit")}
          </button>
        </form>
        <p className="mt-4 text-center text-sm text-slate-400">
          {t("auth.login.newHere")}{" "}
          <Link to="/signup" className="text-brand-300 hover:text-brand-200">
            {t("auth.login.createAccount")}
          </Link>
        </p>
      </div>
    </div>
  );
}
